import { useState, useEffect } from 'react';
import type { MarketIndex, InfraHealth } from '@/types/bot';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api';

// Fallback values if API unavailable
const FALLBACK_INDICES: MarketIndex[] = [
  { name: 'NIFTY', value: 0, change: 0, changePercent: 0, direction: 'up' },
  { name: 'NASDAQ', value: 0, change: 0, changePercent: 0, direction: 'up' },
];

interface MarketData {
  indices: MarketIndex[];
  infraHealth: InfraHealth;
}

export function useMarketData(): MarketData {
  const [indices, setIndices] = useState<MarketIndex[]>(FALLBACK_INDICES);
  const [infraHealth, setInfraHealth] = useState<InfraHealth>({
    networkLatencyMs: 0,
    syncStatus: 'OK',
  });

  useEffect(() => {
    let mounted = true;

    async function fetchIndices() {
      const start = performance.now();
      try {
        const resp = await fetch(`${API_BASE_URL}/market/indices`, { credentials: 'omit' });
        const latency = +(performance.now() - start).toFixed(2);

        if (resp.ok) {
          const data = await resp.json();
          if (mounted && data.indices?.length) {
            setIndices(data.indices);
          }
          if (mounted) {
            setInfraHealth({ networkLatencyMs: latency, syncStatus: 'OK' });
          }
        }
      } catch {
        if (mounted) {
          setInfraHealth(prev => ({ ...prev, syncStatus: 'DEGRADED' }));
        }
      }
    }

    fetchIndices();
    const id = setInterval(fetchIndices, 30000); // Refresh every 30s
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return { indices, infraHealth };
}
